import { useMemo } from 'react'
import {
  BanIcon,
  CircleMinusIcon,
  CirclePlusIcon,
  EllipsisVerticalIcon,
  RotateCcwIcon,
} from 'lucide-react'
import { Chip, IconButton, useTheme } from '@mui/material'

import { ActionDropdown, type ActionDropdownItem } from 'components/input/ActionDropdown/ActionDropdown'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import { useSetCompetitorStatus } from 'hooks/api'
import type {
  SeoCompetitor,
  SeoCompetitorEvidence,
  SeoCompetitorKind,
  SeoKeywordEvidence,
  SeoReviewStatus,
} from 'services'

import { useTone } from '../shared'
import { fmt } from './seoModel'

/**
 * VRG-643 follow-up (Spencer, 2026-10-01) — the review vocabulary shared by every SEO
 * view: competitors and keywords are Approved | Proposed | Rejected. Jobs only PROPOSE;
 * a person approves or rejects, from a kebab on any domain or keyword, or from the
 * Competitors list. Writes are platform-admin only on the server; here a non-admin sees
 * the same menu disabled with the reason (§27/§46).
 *
 * "Rejected" is the UI word only. The stored enum value stays `dismissed` (API, DB
 * `status` columns, CLI, competition.md parser): renaming it is a data migration plus a
 * coordinated API/UI deploy for no behavioural change. Render a status through
 * `REVIEW_LABEL`, never by printing the enum.
 */

const ADMIN_ONLY = 'Only platform admins can change the competitor or keyword set.'

/** What a review status is CALLED. `dismissed` is shown as "Rejected" (see above). */
export const REVIEW_LABEL: Record<SeoReviewStatus, string> = {
  approved: 'Approved',
  proposed: 'Proposed',
  dismissed: 'Rejected',
}

/** Left-to-right order of every status filter (Spencer, 2026-10-01): Approved first. */
export const REVIEW_ORDER: readonly SeoReviewStatus[] = ['approved', 'proposed', 'dismissed']

export const KIND_LABEL: Record<SeoCompetitorKind, string> = {
  business: 'Business',
  search: 'Search',
  adjacent: 'Adjacent',
}

export const KIND_HELP: Record<SeoCompetitorKind, string> = {
  business: 'Sells against Vera.',
  search: 'Ranks for Vera’s terms, whoever they sell to.',
  adjacent: 'Next to Vera, not a direct platform rival: brand protection, security printing, tagless authentication, NFT commerce, RFID materials.',
}

/** A small status chip. Approved renders nothing: it is the default state of the set. */
export const ReviewChip = ({ status }: { status: SeoReviewStatus | null | undefined }) => {
  const theme = useTheme()
  const toneOf = useTone()
  if (status !== 'proposed' && status !== 'dismissed') return null
  // Proposed = the VERA caution tone (orange, via statusTone); Rejected stays disabled grey.
  const tone = status === 'proposed' ? toneOf('caution').color : theme.palette.text.disabled
  return (
    <Chip
      size="small"
      variant="outlined"
      label={REVIEW_LABEL[status]}
      sx={{ height: 20, fontSize: 11, fontWeight: 500, color: tone, borderColor: tone, ml: 1 }}
    />
  )
}

/** One sentence per evidence source, in the order a reviewer weighs them. */
export const competitorEvidenceLines = (e: SeoCompetitorEvidence | null | undefined): string[] => {
  if (!e) return []
  const lines: string[] = []
  for (const [market, s] of Object.entries(e.serp ?? {})) {
    lines.push(
      `${market.toUpperCase()}: top 10 for ${s.terms} tracked terms, best #${s.bestPosition} (${s.examples
        .slice(0, 3)
        .join(', ')}${s.terms > 3 ? ', …' : ''}).`,
    )
  }
  for (const [seed, o] of Object.entries(e.overlap ?? {})) {
    lines.push(
      `Shares ${fmt(o.common)} keywords with ${seed}${
        o.total !== null ? ` (of its ${fmt(o.total)})` : ''
      }, ${o.market.toUpperCase()}.`,
    )
  }
  if (e.note) lines.push(e.note.charAt(0).toUpperCase() + e.note.slice(1) + '.')
  return lines
}

export const keywordEvidenceLine = (e: SeoKeywordEvidence | null | undefined): string | null => {
  const entries = Object.entries(e?.gap ?? {})
  if (!entries.length) return null
  return entries
    .map(([domain, g]) => `${domain}${g.position !== null ? ` #${g.position}` : ''}`)
    .join(', ')
}

/** A 28px kebab trigger: the default 40px one is too tall for a table header or a chip row. */
const SmallKebab = ({
  onClick,
  label,
}: {
  onClick: (e: React.MouseEvent<HTMLElement>) => void
  label: string
}) => (
  <IconButton
    size="small"
    aria-label={label}
    aria-haspopup="true"
    onClick={(e) => {
      e.stopPropagation()
      onClick(e)
    }}
    sx={{ borderRadius: 2, p: '4px', ml: 0.5 }}
  >
    <EllipsisVerticalIcon size={16} strokeWidth={1.5} />
  </IconButton>
)

/**
 * The kebab on every domain: "Add to competitors" (approved) and "Reject"; for an
 * approved domain, "Remove from competitors" (rejected, so no job proposes it again
 * unless its evidence doubles). `kind` applies when the domain has no row yet.
 */
export const CompetitorMenu = ({
  domain,
  status,
  kind,
  size = 'small',
}: {
  domain: string
  status: SeoReviewStatus | null
  kind?: SeoCompetitorKind
  size?: 'small' | 'default'
}) => {
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const setStatus = useSetCompetitorStatus()
  const { open: notify } = useSnackbar()
  const theme = useTheme()

  const run = (next: 'approved' | 'dismissed', done: string) =>
    setStatus.mutate(
      { domain, status: next, kind },
      {
        onSuccess: () => notify({ type: 'success', text: done }),
        onError: (e) => notify({ type: 'error', text: (e as Error).message }),
      },
    )

  const gate = {
    disabled: !isAdmin || setStatus.isPending,
    tooltip: !isAdmin ? ADMIN_ONLY : undefined,
  }
  const items: ActionDropdownItem[] =
    status === 'approved'
      ? [
          {
            label: 'Remove from competitors',
            icon: <CircleMinusIcon size={18} strokeWidth={1} color={theme.palette.text.secondary} />,
            ...gate,
            onClick: () => run('dismissed', `Removed ${domain} from competitors.`),
          },
        ]
      : [
          {
            label: status === 'dismissed' ? 'Restore to competitors' : 'Add to competitors',
            icon:
              status === 'dismissed' ? (
                <RotateCcwIcon size={18} strokeWidth={1} />
              ) : (
                <CirclePlusIcon size={18} strokeWidth={1} />
              ),
            ...gate,
            onClick: () => run('approved', `Added ${domain} to competitors.`),
          },
          {
            label: 'Reject',
            icon: <BanIcon size={18} strokeWidth={1} />,
            visible: status !== 'dismissed',
            ...gate,
            onClick: () => run('dismissed', `Rejected ${domain}. It will not be proposed again unless its evidence doubles.`),
          },
        ]
  return (
    <ActionDropdown
      items={items}
      trigger={
        size === 'small'
          ? ({ onClick }) => <SmallKebab onClick={onClick} label={`Actions for ${domain}`} />
          : undefined
      }
    />
  )
}

/** The kebab on a keyword: "Add to tracked keywords" / "Reject" (or "Remove …"). */
export const KeywordMenu = ({
  keyword,
  status,
  onSet,
  pending,
  extraItems = [],
}: {
  keyword: string
  status: SeoReviewStatus | null
  onSet: (next: 'approved' | 'dismissed') => void
  pending?: boolean
  /** Appended after the review actions (e.g. "Edit tags"). */
  extraItems?: ActionDropdownItem[]
}) => {
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const theme = useTheme()
  const gate = { disabled: !isAdmin || !!pending, tooltip: !isAdmin ? ADMIN_ONLY : undefined }
  const items: ActionDropdownItem[] =
    status === 'approved'
      ? [
          {
            label: 'Remove from tracked keywords',
            icon: <CircleMinusIcon size={18} strokeWidth={1} color={theme.palette.text.secondary} />,
            ...gate,
            onClick: () => onSet('dismissed'),
          },
        ]
      : [
          {
            label: 'Add to tracked keywords',
            icon:
              status === 'dismissed' ? (
                <RotateCcwIcon size={18} strokeWidth={1} />
              ) : (
                <CirclePlusIcon size={18} strokeWidth={1} />
              ),
            ...gate,
            onClick: () => onSet('approved'),
          },
          {
            label: 'Reject',
            icon: <BanIcon size={18} strokeWidth={1} />,
            visible: status !== 'dismissed',
            ...gate,
            onClick: () => onSet('dismissed'),
          },
        ]
  return (
    <ActionDropdown
      items={[...items, ...extraItems]}
      trigger={({ onClick }) => <SmallKebab onClick={onClick} label={`Actions for ${keyword}`} />}
    />
  )
}

/** Review status of a domain against the competitor list (subdomain-aware). */
export const useCompetitorLookup = (competitors: SeoCompetitor[]) =>
  useMemo(() => {
    const map = new Map(competitors.map((c) => [c.domain, c]))
    return (domain: string): SeoCompetitor | null => {
      const d = domain.toLowerCase().replace(/^www\./, '')
      return map.get(d) ?? competitors.find((c) => d.endsWith(`.${c.domain}`)) ?? null
    }
  }, [competitors])
